import Link from 'next/link'

import Container from '@/components/base/container'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { articleHeroRevealAnimation } from '@/components/sections/animations/article-hero'
import Spacer from '@/components/sections/spacer'
import useStableViewport from '@/hooks/use-stable-viewport'
import { formatDate } from '@/utils/date'

import { cn } from '@/lib/utils'
import { WPImage, WPPageData } from '@/types'

type ArticleHeroProps = {
  pageProps: WPPageData & {
    article_hero?: {
      title?: string
      date?: string
      image?: WPImage
      image_mobile?: WPImage
      category?: {
        title?: string
        link?: string
      }[]
    }
  }
}

const ArticleHero = ({ pageProps }: ArticleHeroProps) => {
  const { isMobile } = useStableViewport()
  const { title, date, image, image_mobile, category } =
    pageProps.article_hero || {}

  return (
    <Motion animation={articleHeroRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef} data-layout="article_hero">
          <Spacer variant="xxl" />
          <Container>
            <div
              className={cn(
                'block items-end justify-between space-y-spacing-md md:flex'
              )}
            >
              <div className={cn('flex')}>
                <Typography variant="h2" component="h1" data-motion-title>
                  {title}
                </Typography>
              </div>
              <div
                className={cn(
                  'text-regular text-trim flex gap-spacing-sm text-text-primary-light md:translate-y-[.7rem]'
                )}
              >
                {date ? <div className="flex">{formatDate(date)}</div> : null}
                {category &&
                  category.length > 0 &&
                  category.map((cat, index) => (
                    <div className="flex" key={index}>
                      <Link
                        href={cat.link || '#'}
                        scroll={false}
                        className={cn('')}
                      >
                        {cat.title}
                      </Link>
                    </div>
                  ))}
              </div>
            </div>
          </Container>
          <div
            className={cn('mt-d-spacer-xs md:mt-d-spacer-md')}
            data-motion-media
          >
            <img
              src={
                isMobile && image_mobile?.url ? image_mobile?.url : image?.url
              }
              alt={
                isMobile && image_mobile?.alt ? image_mobile?.alt : image?.alt
              }
              fetchPriority="high"
              className="h-auto w-full object-cover object-center"
            />
          </div>
        </Section>
      )}
    </Motion>
  )
}

export default ArticleHero
